import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import { showErrorToast, showSuccessToast } from "../../utils/toaster";
import Loader from "../Loader";
import { baseurl } from "../../config/url";
import AddProductDialog from "./AddProductBiilling";
import { calculateHireVisibilityCharges } from "../../utils/calculateVisibilityCharges";

/**
 * ✅ AddProductForm — UI/UX Upgrade ONLY (logic unchanged)
 * - Professional layout (cards/sections)
 * - Consistent palette (white/gray + blue)
 * - Better spacing + responsive grid
 * - States: Search + "Select All" + "Clear" + checkbox list in scroll panel
 * - Images: nicer previews + remove buttons
 * - Specs: nicer add row + list
 *
 * NOTE: I did NOT change API logic / validation logic / payload logic.
 */

const AddProductForm = ({
  productName,
  setProductName,
  productDescription,
  setProductDescription,
  productPrice,
  setProductPrice,
  productSize,
  setProductSize,
  maxquantity,
  setMaxquantity,
  minquantity,
  setMinquantity,
  stock,
  setStock,
  saleMode,
  setSaleMode,
  setImage,
  selectedCategory,
  selectedSubcategory,
  image,
  setImages,
  images,
  deliveryPreference,
  setDeliveryPreference,
  selectedCategoryName,
  selectedSubcategoryName,
}) => {
  const [powerPackVisibility, setPowerPackVisibility] = useState(false);
  const [priority, setPriority] = useState({ is1XPriority: false, is3XPriority: false });
  const [saleProduct, setSaleProduct] = useState("Sale");
  const [visibilityLevel, setVisibilityLevel] = useState("");
  const [deliveryCharges, setDeliveryCharges] = useState({
    baseCharge: "",
    perKmCharge: "",
    weightCharge: "",
    maxDistance: "",
  });

  const [saleStates, setSaleStates] = useState([]);
  const [selectedHireType, setSelectedHireType] = useState("");
  const [hireChargeAmount, setHireChargeAmount] = useState("");

  const statesList = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDeliveryPreference((prev) => ({ ...prev, [name]: value }));
  };

  const handlevideoPrivorityCheckboxChange = (key) => {
    setPriority((prevState) => ({ ...prevState, [key]: !prevState[key] }));
  };

  const handleStateSelection = (state) => {
    setSaleStates((prevStates) =>
      prevStates.includes(state)
        ? prevStates.filter((s) => s !== state)
        : [...prevStates, state]
    );
  };

  // ✅ NEW UI helpers (no business logic change)
  const [stateQuery, setStateQuery] = useState("");
  const visibleStates = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    if (!q) return statesList;
    return statesList.filter((s) => s.toLowerCase().includes(q));
  }, [stateQuery]);

  const allStatesSelected = useMemo(
    () => saleStates.length === statesList.length,
    [saleStates.length]
  );

  const selectAllStates = () => setSaleStates(statesList);
  const clearAllStates = () => setSaleStates([]);
  const toggleSelectAllStates = () => {
    if (allStatesSelected) clearAllStates();
    else selectAllStates();
  };

  const [priceUnit, setPriceUnit] = useState("");
  const [specifications, setSpecifications] = useState([]);
  const [newSpec, setNewSpec] = useState({ key: "", value: "" });
  const [loader, setloader] = useState(false);
  const [gstnumber, setGstnumber] = useState("");
  const [hsncode, setHsncode] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleVisibilityChange = (value) => setVisibilityLevel(value);

  const validUnits = [
    "Perkg",
    "PerNumber",
    "PerMeter",
    "PerLiter",
    "PerCum",
    "PerCft",
    "PerMT",
    "PerBox",
    "PerRF",
    "PerRM",
    "PerLtr",
    "PerSqft",
    "perBundle",
    "PerRoll",
    "PerTan",
    "PerBags",
  ];

  const [hireDuration, setHireDuration] = useState({ value: "", unit: "days" });

  const handleAddSpecification = () => {
    if (newSpec.key && newSpec.value) {
      setSpecifications((prev) => [...prev, newSpec]);
      setNewSpec({ key: "", value: "" });
    } else {
      showErrorToast("Both key and value are required for a specification.");
    }
  };

  const handlePriceUnitChange = (e) => setPriceUnit(e.target.value);

  const handleDeleteSpecification = (index) => {
    const updated = specifications.filter((_, i) => i !== index);
    setSpecifications(updated);
  };

  const validateForm = () => {
    if (saleProduct === "Hire") {
      if (!selectedHireType || !hireChargeAmount) {
        showErrorToast("Please select hire type and enter charge.");
        return false;
      }
    }

    if (!selectedCategory) {
      showErrorToast("Please select a category.");
      return;
    }
    if (!selectedSubcategory) {
      showErrorToast("Please select a subcategory.");
      return;
    }
    if (!productName) {
      showErrorToast("Product Name is required.");
      return false;
    }
    if (!productDescription) {
      showErrorToast("Product Description is required.");
      return false;
    }
    if (!productSize) {
      showErrorToast("Product Size is required.");
      return false;
    }
    if (!minquantity) {
      showErrorToast("Minimum Quantity is required.");
      return false;
    }
    if (!maxquantity) {
      showErrorToast("Maximum Quantity is required.");
      return false;
    }

    if (!productPrice || +productPrice <= 0) {
      showErrorToast("Please enter a valid product price.");
      return false;
    }

    if (!priceUnit || priceUnit === "Select Price Unit") {
      showErrorToast("Please select a price unit.");
      return false;
    }

    if (!stock || +stock < 0) {
      showErrorToast("Please enter a valid stock quantity.");
      return false;
    }
    if (+minquantity > +maxquantity) {
      showErrorToast("Minimum quantity cannot exceed maximum quantity.");
      return false;
    }

    if (+maxquantity < +minquantity) {
      showErrorToast("Maximum quantity cannot be less than minimum quantity.");
      return false;
    }

    if (+maxquantity > +stock) {
      showErrorToast("Maximum quantity cannot exceed stock quantity.");
      return false;
    }

    if (!gstnumber) {
      showErrorToast("GST Number is required.");
      return false;
    }

    if (!hsncode) {
      showErrorToast("HSN Code is required.");
      return false;
    }

    if (!saleMode) {
      showErrorToast("Sale Mode is required.");
      return false;
    }
    if (saleMode === "Sale By Seller" && !visibilityLevel) {
      showErrorToast("Visibility Level is required when Sale Mode is 'Sale By Seller'.");
      return false;
    }

    // Delivery Preference Validation
    if (!deliveryPreference.duration || +deliveryPreference.duration <= 0) {
      showErrorToast("Please provide a valid delivery duration.");
      return false;
    }
    if (!deliveryPreference.unit) {
      showErrorToast("Please select a delivery unit (e.g., days, weeks, months, years).");
      return false;
    }
    if (saleMode == "Sale By Seller" && !visibilityLevel) {
      showErrorToast("Visibility Level is required.");
      return false;
    }

    if (!image) {
      showErrorToast("Image is required.");
      return false;
    }
    if (images.length == 0) {
      showErrorToast("At least one additional image is required.");
      return false;
    }

    if (saleMode === "Sale By OGCS") {
      const { baseCharge, perKmCharge, weightCharge, maxDistance } = deliveryCharges;
      if (!baseCharge || baseCharge < 0) {
        showErrorToast("Base charge must be a valid number.");
        return false;
      }
      if (!perKmCharge || perKmCharge < 0) {
        showErrorToast("Per KM charge must be a valid number.");
        return false;
      }
      if (!weightCharge || weightCharge < 0) {
        showErrorToast("Weight charge must be a valid number.");
        return false;
      }
      if (!maxDistance || maxDistance < 0) {
        showErrorToast("Max distance must be a valid number.");
        return false;
      }
    }

    if (specifications.length < 1) {
      showErrorToast("You must provide at least 6 specifications.");
      return false;
    }

    if (saleMode !== "Sale By BestPrice" && (!productPrice || +productPrice <= 0)) {
      showErrorToast("Please enter a valid product price.");
      return false;
    }

    return true;
  };

  const createdeliverycharges = async (productId) => {
    try {
      const token = localStorage.getItem("token");
      const sellerId = localStorage.getItem("sellerId");
      if (!token || !sellerId) {
        showErrorToast("You must be logged in to create delivery charges.");
        return;
      }

      const deliveryData = {
        productId,
        seller: sellerId,
        baseCharge: deliveryCharges.baseCharge,
        perKmCharge: deliveryCharges.perKmCharge,
        weightCharge: deliveryCharges.weightCharge,
        maxDistance: deliveryCharges.maxDistance,
      };

      const response = await axios.post(`${baseurl}/api/deliverycharge`, deliveryData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 201) showSuccessToast("Delivery charges created successfully.");
      else showErrorToast("Failed to create delivery charges. Please try again.");
    } catch (error) {
      console.error("Error creating delivery charges:", error.response?.data || error.message);
      showErrorToast("Failed to create delivery charges. Please try again.");
    } finally {
      setDeliveryCharges({ baseCharge: "", perKmCharge: "", weightCharge: "", maxDistance: "" });
      setloader(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setDialogOpen(true);
  };

  const handlePopupSubmit = async (data, storeSellerVisibilityPayment) => {
    const token = localStorage.getItem("token");
    if (!token) {
      showErrorToast("You must be logged in to submit a product.");
      return;
    }

    if (saleProduct === "Hire" && saleMode === "Sale By Seller") {
      const hireCharge = Number(hireChargeAmount);
      let monthlyHireValue;

      if (selectedHireType === "days") monthlyHireValue = hireCharge * 30;
      else if (selectedHireType === "weeks") monthlyHireValue = hireCharge * 4.2857;
      else monthlyHireValue = hireCharge;

      const visibilityCharges = calculateHireVisibilityCharges(monthlyHireValue, visibilityLevel);
      console.log("Calculated Visibility Charges:", visibilityCharges);
    }

    let videoPriority = [];
    if (priority.is1XPriority) videoPriority.push("1XVIDEO");
    if (priority.is3XPriority) videoPriority.push("3XVIDEO");

    setloader(true);

    try {
      const formData = new FormData();
      formData.append("name", productName);
      formData.append("description", productDescription);
      formData.append("price", productPrice);
      formData.append("priceUnit", priceUnit);
      formData.append("size", productSize);
      formData.append("stock", stock);
      formData.append("maxquantity", maxquantity);
      formData.append("minquantity", minquantity);
      formData.append("gstNumber", gstnumber);
      formData.append("hsnCode", hsncode);
      formData.append("saleType", saleMode);
      formData.append("visibilityLevel", visibilityLevel);
      formData.append("category", selectedCategory);
      formData.append("subcategory", selectedSubcategory);
      formData.append("PowerPackVisibility", powerPackVisibility);
      formData.append("videoPriority", JSON.stringify(videoPriority));
      formData.append("saleProduct", saleProduct);

      saleStates.forEach((state, index) => formData.append(`states[${index}]`, state));

      if (saleProduct === "Hire") {
        if (selectedHireType && hireChargeAmount) {
          formData.append(`hireCharges[${selectedHireType}]`, hireChargeAmount);
        } else {
          showErrorToast("Please select a hire type and enter the charge.");
          return;
        }

        formData.append("hireDuration[value]", hireDuration.value || 0);
        formData.append("hireDuration[unit]", hireDuration.unit || "days");
      }

      if (images && images.length > 0) {
        images.forEach((img) => formData.append(`files`, img));
      }
      if (image) formData.append("files", image);

      specifications.forEach((spec, index) => {
        formData.append(`specifications[${index}][key]`, spec.key);
        formData.append(`specifications[${index}][value]`, spec.value);
      });

      Object.entries(deliveryPreference).forEach(([key, value]) => {
        formData.append(`deliveryPreference[${key}]`, value);
      });

      const response = await axios.post(`${baseurl}/api/seller/sell-product`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      if (response.status === 201 && saleMode === "Sale By OGCS") {
        createdeliverycharges(response.data.product._id);

        await axios.post(
          `${baseurl}/api/seller/send-invoise-email`,
          {
            sellerEmail: localStorage.getItem("sellerEmail"),
            productName: response.data.product.name,
            maxQuantity: maxquantity,
            visibilityLevel,
            visibilityCharges: saleMode === "Sale By Seller" ? data.visibilityCharges : 0,
            depositCharges: saleMode === "Sale By OGCS" ? 0.125 * productPrice * maxquantity : 0,
            saleMode,
          },
          {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );

        showSuccessToast("Product and Invoice email successfully sent!");
        showSuccessToast("Product stored successfully.");
        return;
      }

      storeSellerVisibilityPayment(response.data.product._id, data);
      showSuccessToast("Product stored successfully.");
    } catch (error) {
      // keep your behavior (no toast)
    } finally {
      setloader(false);

      setProductName("");
      setProductDescription("");
      setProductPrice("");
      setProductSize("");
      setStock("");
      setMinquantity("");
      setMaxquantity("");
      setGstnumber("");
      setHsncode("");
      setVisibilityLevel("");
      setSaleMode("");
      setImage(null);
      setSpecifications([]);
      setPowerPackVisibility("");
      setImage([]);
      setDeliveryPreference({ duration: "", unit: "days" });
      setDialogOpen(false);
    }
  };

  const handleDeliveryChargesChange = (field, value) => {
    setDeliveryCharges((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event) => {
    const selectedImages = Array.from(event.target.files);
    const updatedImages = [...images];

    for (let i = 0; i < selectedImages.length; i++) {
      const file = selectedImages[i];

      if (file.size > 102400) {
        showErrorToast(`Image "${file.name}" exceeds 100KB limit.`);
        continue;
      }

      if (updatedImages.length >= 5) {
        showErrorToast("You can only add up to 5 images.");
        break;
      }

      updatedImages.push(file);
    }

    setImages(updatedImages);
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (saleProduct !== "Hire") {
      setSelectedHireType("");
      setHireChargeAmount("");
      setHireDuration({ value: "", unit: "days" });
    }
  }, [saleProduct]);

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Product</h2>
            <p className="text-sm text-gray-600 mt-1">
              Fill details carefully. Images must be under 100KB each.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-700">
              Category: {selectedCategoryName || "—"}
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-700">
              Sub: {selectedSubcategoryName || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Main Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Product Details */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900">Product Details</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-gray-700">
                Product Name
                <input
                  type="text"
                  required
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Enter product name"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Price
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    required
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    placeholder="Enter price"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <select
                    value={priceUnit}
                    onChange={handlePriceUnitChange}
                    className="min-w-[160px] px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">--Select Unit--</option>
                    {validUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="md:col-span-2 text-sm font-medium text-gray-700">
                Product Description
                <input
                  type="text"
                  required
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  placeholder="Enter description"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Stock
                <input
                  type="number"
                  required
                  value={stock}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) setStock(value);
                  }}
                  placeholder="Enter stock"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Size
                <input
                  type="text"
                  required
                  value={productSize}
                  onChange={(e) => setProductSize(e.target.value)}
                  placeholder="Enter size"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Minimum Quantity
                <input
                  type="number"
                  required
                  value={minquantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) setMinquantity(value);
                  }}
                  placeholder="Enter minimum quantity"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Maximum Quantity
                <input
                  type="number"
                  required
                  value={maxquantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) setMaxquantity(value);
                  }}
                  placeholder="Enter maximum quantity"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-gray-700">
                GST (%)
                <input
                  type="number"
                  required
                  min={0}
                  value={gstnumber}
                  onChange={(e) => setGstnumber(e.target.value)}
                  placeholder="e.g., 18"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                HSN Code
                <input
                  type="text"
                  required
                  value={hsncode}
                  onChange={(e) => setHsncode(e.target.value)}
                  placeholder="Enter HSN code"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            </div>
          </div>

          {/* Sale & Visibility */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900">Sale & Visibility</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-gray-700">
                Sale Mode
                <select
                  value={saleMode}
                  required
                  onChange={(e) => {
                    setSaleMode(e.target.value);
                    if (e.target.value == "Sale By OGCS") handleVisibilityChange("");
                  }}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">--Select--</option>
                  <option value="Sale By Seller">Sale by Seller</option>
                  <option value="Sale By OGCS">Sale by OGCS</option>
                  <option value="Sale By BestPrice">Sale By BestPrice</option>
                </select>
              </label>

              {saleMode == "Sale By Seller" && (
                <label className="text-sm font-medium text-gray-700">
                  Visibility Level
                  <select
                    name="visibilityLevel"
                    value={visibilityLevel}
                    onChange={(e) => handleVisibilityChange(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">--Select Visibility Level--</option>
                    <option value="1X">1X</option>
                    <option value="2X">2X</option>
                    <option value="3X">3X</option>
                    <option value="4X">4X</option>
                  </select>
                </label>
              )}

              {saleMode == "Sale By Seller" && (
                <label className="text-sm font-medium text-gray-700">
                  Product Sale Type
                  <select
                    value={saleProduct}
                    onChange={(e) => setSaleProduct(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    required
                  >
                    <option value="">--Select Sale Type--</option>
                    <option value="Sale">Sale</option>
                    <option value="Hire">Hire</option>
                  </select>
                </label>
              )}
            </div>

            {saleProduct === "Hire" && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900">Hire Charges</div>

                  <label className="mt-3 block text-sm font-medium text-gray-700">
                    Select Hire Type
                    <select
                      value={selectedHireType}
                      onChange={(e) => setSelectedHireType(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="">--Select--</option>
                      <option value="days">Per Day</option>
                      <option value="weeks">Per Week</option>
                      <option value="months">Per Month</option>
                    </select>
                  </label>

                  <label className="mt-3 block text-sm font-medium text-gray-700">
                    Hire Charge Amount
                    <input
                      type="number"
                      min={0}
                      value={hireChargeAmount}
                      onChange={(e) => setHireChargeAmount(e.target.value)}
                      placeholder="e.g., 500"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900">Hire Duration</div>

                  <label className="mt-3 block text-sm font-medium text-gray-700">
                    Duration
                    <input
                      type="number"
                      value={hireDuration.value}
                      onChange={(e) =>
                        setHireDuration((prev) => ({ ...prev, value: e.target.value }))
                      }
                      placeholder="e.g., 5"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>

                  <label className="mt-3 block text-sm font-medium text-gray-700">
                    Unit
                    <select
                      value={hireDuration.unit}
                      onChange={(e) =>
                        setHireDuration((prev) => ({ ...prev, unit: e.target.value }))
                      }
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                  </label>
                </div>
              </div>
            )}

            {/* PowerPack */}
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    PowerPack Visibility
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    If enabled, product will be shown on homepage and may bypass product
                    detail page directly to <strong>Add to Cart</strong>.
                  </p>
                </div>

                <label className="inline-flex items-center gap-2 select-none">
                  <span className="text-xs font-semibold text-gray-600">
                    {powerPackVisibility ? "ON" : "OFF"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPowerPackVisibility((v) => !v)}
                    className={[
                      "relative w-14 h-8 rounded-full transition",
                      powerPackVisibility ? "bg-blue-600" : "bg-gray-300",
                    ].join(" ")}
                    aria-label="Toggle PowerPack Visibility"
                  >
                    <span
                      className={[
                        "absolute top-1 w-6 h-6 rounded-full bg-white shadow transition",
                        powerPackVisibility ? "left-7" : "left-1",
                      ].join(" ")}
                    />
                  </button>
                </label>
              </div>
            </div>
          </div>

          {/* Delivery Preference + OGCS Charges */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900">Delivery</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-gray-700">
                Estimated Delivery Duration
                <input
                  type="number"
                  min={0}
                  name="duration"
                  value={deliveryPreference.value}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Delivery Unit
                <select
                  name="unit"
                  value={deliveryPreference.unit}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </label>
            </div>

            {saleMode === "Sale By OGCS" && (
              <div className="mt-4">
                <div className="text-sm font-semibold text-gray-900">Delivery Charges</div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-sm font-medium text-gray-700">
                    Base Charge
                    <input
                      required
                      type="number"
                      value={deliveryCharges.baseCharge}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value))
                          handleDeliveryChargesChange("baseCharge", value);
                      }}
                      placeholder="Enter base charge"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>

                  <label className="text-sm font-medium text-gray-700">
                    Per Km Charge
                    <input
                      required
                      type="number"
                      value={deliveryCharges.perKmCharge}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value))
                          handleDeliveryChargesChange("perKmCharge", value);
                      }}
                      placeholder="Enter per km charge"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>

                  <label className="text-sm font-medium text-gray-700">
                    Weight Charge
                    <input
                      required
                      type="number"
                      value={deliveryCharges.weightCharge}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value))
                          handleDeliveryChargesChange("weightCharge", value);
                      }}
                      placeholder="Enter weight charge"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>

                  <label className="text-sm font-medium text-gray-700">
                    Max Km Distance
                    <input
                      required
                      type="number"
                      value={deliveryCharges.maxDistance}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value))
                          handleDeliveryChargesChange("maxDistance", value);
                      }}
                      placeholder="Enter max distance"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Images */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900">Images</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-gray-700">
                Main Product Image (Max 100KB)
                <input
                  type="file"
                  required
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 102400) {
                        showErrorToast("Main image must be less than 100KB.");
                        return;
                      }
                      setImage(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const imgPreview = document.getElementById("image-preview");
                        if (imgPreview) imgPreview.src = reader.result;
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Additional Images (each &lt; 100KB, max 5)
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              {/* main preview */}
              {image && (
                <div className="relative">
                  <div className="w-24 h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                      id="image-preview"
                      alt="Selected Product"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center shadow hover:bg-red-700 transition"
                    title="Remove"
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              )}

              {/* additional previews */}
              {images?.length > 0 &&
                images.map((img, index) => (
                  <div key={index} className="relative">
                    <div className="w-24 h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center shadow hover:bg-red-700 transition"
                      title="Remove"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-gray-900">Specifications</h3>
              <span className="text-xs font-semibold px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
                Added: {specifications.length}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={newSpec.key}
                onChange={(e) => setNewSpec({ ...newSpec, key: e.target.value })}
                placeholder="Key (e.g., Brand)"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <input
                type="text"
                value={newSpec.value}
                onChange={(e) => setNewSpec({ ...newSpec, value: e.target.value })}
                placeholder="Value (e.g., OGCS)"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                type="button"
                onClick={handleAddSpecification}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow-sm"
              >
                Add
              </button>
            </div>

            {specifications?.length > 0 && (
              <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                <div className="max-h-[220px] overflow-auto">
                  {specifications.map((spec, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-gray-200 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {spec.key}
                        </div>
                        <div className="text-xs text-gray-600 truncate">{spec.value}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteSpecification(index)}
                        className="w-9 h-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition"
                        title="Delete"
                      >
                        <FaTrash className="text-gray-700" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: States + Submit */}
        <div className="space-y-4">
          {/* States */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Sale States</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select states where this product is available.
                </p>
              </div>
              <span className="text-xs font-semibold px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
                Selected: {saleStates.length}
              </span>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <input
                value={stateQuery}
                onChange={(e) => setStateQuery(e.target.value)}
                placeholder="Search state..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={toggleSelectAllStates}
                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
              >
                {allStatesSelected ? "Unselect All" : "Select All"}
              </button>
              <button
                type="button"
                onClick={clearAllStates}
                className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition"
              >
                Clear
              </button>
            </div>

            <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
              <div className="max-h-[360px] overflow-auto p-3 bg-gray-50">
                {visibleStates.map((state) => {
                  const checked = saleStates.includes(state);
                  return (
                    <label
                      key={state}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 transition cursor-pointer mb-2 last:mb-0"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {state}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleStateSelection(state)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400"
                      />
                    </label>
                  );
                })}

                {visibleStates.length === 0 && (
                  <div className="text-sm text-gray-600 text-center py-6">
                    No states match your search.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow-sm flex items-center justify-center"
            >
              {loader ? <Loader /> : "Submit Product"}
            </button>

            <p className="mt-3 text-xs text-gray-500">
              Please confirm all details before submitting.
            </p>
          </div>
        </div>
      </div>

      <AddProductDialog
        open={dialogOpen}
        priority={priority}
        handlevideoPrivorityCheckboxChange={handlevideoPrivorityCheckboxChange}
        setVisibilityLevel={setVisibilityLevel}
        visibilityLevel={visibilityLevel}
        handlePopupSubmit={handlePopupSubmit}
        setDialogOpen={setDialogOpen}
        powerPackVisibility={powerPackVisibility}
        setPowerPackVisibility={setPowerPackVisibility}
        ammount={productPrice}
        maxquantity={maxquantity}
        saleMode={saleMode}
        categoryName={selectedCategoryName}
        subcategoryName={selectedSubcategoryName}
        products={[
          {
            productName,
            productPrice,
            maxquantity,
            visibilityLevel,
            hsncode,
            saleProduct,
            hireChargeAmount,
            hireDuration,
            selectedHireType,
          },
        ]}
      />
    </div>
  );
};

export default AddProductForm;